import { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapbox } from '../../hooks/useMapbox';
import { CountryLayer } from './CountryLayer';
import { PlaceMarkers, PLACES_LAYER_ID } from './PlaceMarkers';
import { MapControls } from './MapControls';
import type { Place, CountryVisit } from '../../types';

// Cache the country GeoJSON so we only fetch it once
let countryGeoJsonCache: GeoJSON.FeatureCollection | null = null;
async function getCountryGeoJson(): Promise<GeoJSON.FeatureCollection> {
  if (countryGeoJsonCache) return countryGeoJsonCache;
  const res = await fetch('/world-countries.geojson');
  countryGeoJsonCache = await res.json();
  return countryGeoJsonCache!;
}

interface MapViewProps {
  places: Place[];
  countries: CountryVisit[];
  focusPlace: Place | null;
  focusCountryCode?: string | null;
  onPlaceClick?: (place: Place) => void;
  onMapClick?: (lng: number, lat: number) => void;
}

export function MapView({ places, countries, focusPlace, focusCountryCode, onPlaceClick, onMapClick }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { map, mapReady } = useMapbox(containerRef);

  // Fly to focused place (from sidebar click)
  useEffect(() => {
    if (!map || !mapReady || !focusPlace) return;
    map.flyTo({
      center: [focusPlace.Longitude, focusPlace.Latitude],
      zoom: Math.max(map.getZoom(), 4),
      duration: 1200,
    });
  }, [map, mapReady, focusPlace]);

  // Countries with distant overseas territories — only include mainland coordinates
  // [minLng, minLat, maxLng, maxLat]
  const MAINLAND_BOUNDS: Record<string, [number, number, number, number]> = {
    FR: [-6, 41, 10, 52],      // Metropolitan France (excludes French Guiana, Réunion, etc.)
    NL: [3, 50.5, 7.5, 54],    // European Netherlands (excludes Caribbean islands)
  };

  // Fly to country bounds (from sidebar country click)
  useEffect(() => {
    if (!map || !mapReady || !focusCountryCode) return;
    let cancelled = false;
    getCountryGeoJson().then((geojson) => {
      if (cancelled) return;
      const matching = geojson.features.filter(
        (f) => f.properties?.['ISO3166-1-Alpha-2'] === focusCountryCode
      );
      if (matching.length === 0) return;
      const clamp = MAINLAND_BOUNDS[focusCountryCode];
      let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
      const processCoord = (coord: number[]) => {
        // Skip coordinates outside mainland bounds if defined
        if (clamp && (coord[0] < clamp[0] || coord[1] < clamp[1] || coord[0] > clamp[2] || coord[1] > clamp[3])) return;
        if (coord[0] < minLng) minLng = coord[0];
        if (coord[0] > maxLng) maxLng = coord[0];
        if (coord[1] < minLat) minLat = coord[1];
        if (coord[1] > maxLat) maxLat = coord[1];
      };
      for (const feat of matching) {
        const geom = feat.geometry;
        if (geom.type === 'Polygon') {
          for (const ring of (geom as GeoJSON.Polygon).coordinates) for (const c of ring) processCoord(c);
        } else if (geom.type === 'MultiPolygon') {
          for (const poly of (geom as GeoJSON.MultiPolygon).coordinates) for (const ring of poly) for (const c of ring) processCoord(c);
        }
      }
      if (minLng < maxLng && minLat < maxLat) {
        map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 100, duration: 1200, maxZoom: 6 });
      }
    });
    return () => { cancelled = true; };
  }, [map, mapReady, focusCountryCode]);

  // Keep stable refs so the handler doesn't re-register on every render
  const placesRef = useRef(places);
  const onPlaceClickRef = useRef(onPlaceClick);
  placesRef.current = places;
  onPlaceClickRef.current = onPlaceClick;

  useEffect(() => {
    if (!map || !mapReady || !onMapClick) return;
    const CLICK_BUFFER = 25; // px tolerance around click point
    const handler = (e: mapboxgl.MapMouseEvent) => {
      // Skip if a place marker was clicked (it calls stopPropagation on originalEvent)
      if (e.originalEvent.cancelBubble) return;
      // Check for place features within a generous buffer around the click
      const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - CLICK_BUFFER, e.point.y - CLICK_BUFFER],
        [e.point.x + CLICK_BUFFER, e.point.y + CLICK_BUFFER],
      ];
      const features = map.queryRenderedFeatures(bbox, { layers: [PLACES_LAYER_ID] });
      if (features.length > 0 && onPlaceClickRef.current) {
        // Find the nearest place to the click point
        let nearest = features[0];
        let minDist = Infinity;
        for (const f of features) {
          if (f.geometry.type !== 'Point') continue;
          const projected = map.project(f.geometry.coordinates as [number, number]);
          const dx = projected.x - e.point.x;
          const dy = projected.y - e.point.y;
          const dist = dx * dx + dy * dy;
          if (dist < minDist) { minDist = dist; nearest = f; }
        }
        const placeId = (nearest.properties as Record<string, string>).PlaceId;
        const place = placesRef.current.find((p) => p.PlaceId === placeId);
        if (place) {
          map.flyTo({ center: [place.Longitude, place.Latitude], zoom: Math.max(map.getZoom(), 7), duration: 800 });
          onPlaceClickRef.current(place);
          return;
        }
      }
      onMapClick(e.lngLat.lng, e.lngLat.lat);
    };
    map.on('click', handler);
    return () => { map.off('click', handler); };
  }, [map, mapReady, onMapClick]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <CountryLayer map={map} mapReady={mapReady} countries={countries} />
      <PlaceMarkers map={map} mapReady={mapReady} places={places} onPlaceClick={onPlaceClick} />
      <MapControls map={map} />
    </div>
  );
}
